import { Phone, Zap, Brain, Plus, MessageSquare, Code, Database, Mail, FileText, Calculator, Link, Palette, User, PhoneCall, Clock, CheckCircle, AlertCircle, ArrowDown, ArrowRight } from "lucide-react";

interface ServicesProps {
  homepageData?: {
    servicesNumber?: string;
    servicesHeadline?: string;
  };
}

export const Services = ({ homepageData }: ServicesProps) => {
  return <section className="py-20 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Section 001 */}
        <div className="mb-20">
          <div className="text-sm text-purple-500 font-medium mb-4">{homepageData?.servicesNumber || "001"}</div>
          <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-16 max-w-4xl">{homepageData?.servicesHeadline || "Automate More. Spend Less. Achieve Faster."}</h2>
          
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="bg-gray-100 rounded-lg p-8 h-80 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-900 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <Phone className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-sm text-gray-600 mb-2">Mobile</div>
                  <div className="text-sm text-gray-600">Customer</div>
                  <div className="mt-4 flex space-x-2 justify-center">
                    <div className="w-8 h-8 bg-red-500 rounded-full"></div>
                    <div className="w-8 h-8 bg-green-500 rounded-full"></div>
                  </div>
                  <div className="mt-4 text-xs text-gray-500">Your AI Voice Agent</div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-900 rounded-lg p-8 h-80 text-white relative overflow-hidden flex items-center justify-center">
              {/* Tree Diagram - Centered */}
              <div className="flex flex-col items-center space-y-4 text-xs">
                {/* Root - Incoming Call */}
                <div className="flex items-center space-x-2 bg-purple-600 px-3 py-1 rounded-full">
                  <PhoneCall className="w-3 h-3" />
                  <span>Incoming Call</span>
                </div>
                
                <ArrowDown className="w-4 h-4 text-gray-400" />
                
                {/* AI Reception */}
                <div className="flex items-center space-x-2 bg-blue-600 px-3 py-1 rounded-full">
                  <Brain className="w-3 h-3" />
                  <span>AI Greeting & Intent Detection</span>
                </div>
                
                <ArrowDown className="w-4 h-4 text-gray-400" />
                
                {/* Branch Point */}
                <div className="flex items-center justify-center space-x-8">
                  {/* Support Branch */}
                  <div className="flex flex-col items-center space-y-2">
                    <div className="flex items-center space-x-1 bg-green-600 px-2 py-1 rounded text-xs">
                      <User className="w-3 h-3" />
                      <span>Support</span>
                    </div>
                    <ArrowDown className="w-3 h-3 text-gray-400" />
                    <div className="flex items-center space-x-1 bg-green-500 px-2 py-1 rounded text-xs">
                      <CheckCircle className="w-3 h-3" />
                      <span>Resolve</span>
                    </div>
                  </div>
                  
                  {/* Sales Branch */}
                  <div className="flex flex-col items-center space-y-2">
                    <div className="flex items-center space-x-1 bg-orange-600 px-2 py-1 rounded text-xs">
                      <MessageSquare className="w-3 h-3" />
                      <span>Sales</span>
                    </div>
                    <ArrowDown className="w-3 h-3 text-gray-400" />
                    <div className="flex items-center space-x-1 bg-orange-500 px-2 py-1 rounded text-xs">
                      <Database className="w-3 h-3" />
                      <span>CRM</span>
                    </div>
                  </div>
                  
                  {/* Escalation Branch */}
                  <div className="flex flex-col items-center space-y-2">
                    <div className="flex items-center space-x-1 bg-red-600 px-2 py-1 rounded text-xs">
                      <AlertCircle className="w-3 h-3" />
                      <span>Complex</span>
                    </div>
                    <ArrowDown className="w-3 h-3 text-gray-400" />
                    <div className="flex items-center space-x-1 bg-red-500 px-2 py-1 rounded text-xs">
                      <User className="w-3 h-3" />
                      <span>Human</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="absolute bottom-4 right-4">
                <div className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs">
                  24/7 AI Service
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* AI Agents and Copilots */}
        <div className="grid md:grid-cols-2 gap-16 mb-20">
          <div>
            <h3 className="text-2xl font-medium text-gray-900 mb-4">
              AI phone agents that work 24/7.
            </h3>
            <p className="text-gray-600 mb-8">
              Operations? Sales? Customer Support? We automate your customer interactions 
              with <a href="/blog/implementing-ai-in-your-customer-service-strategy" className="text-violet-600 hover:text-violet-700 underline">voice-based agents</a> that provide natural, human-like conversations, without 
              the wait time.
            </p>
            
            <div className="bg-gray-100 rounded-lg p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                <div className="text-sm font-medium">INPUTS</div>
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <div className="text-sm font-medium text-purple-600">AI AGENTS</div>
                <div className="w-3 h-3 bg-gray-900 rounded-full"></div>
                <div className="text-sm font-medium">TASKS</div>
              </div>
              
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-xs font-medium mb-2">MODELS</div>
                  <div className="w-8 h-8 bg-gray-300 rounded mx-auto flex items-center justify-center">
                    <Brain className="h-4 w-4" />
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium mb-2">MODELS</div>
                  <div className="w-8 h-8 bg-gray-300 rounded mx-auto flex items-center justify-center">
                    <Zap className="h-4 w-4" />
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium mb-2">TOOLS</div>
                  <div className="w-8 h-8 bg-gray-300 rounded mx-auto flex items-center justify-center">
                    <Plus className="h-4 w-4" />
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium mb-2">MEMORY</div>
                  <div className="w-8 h-8 bg-gray-300 rounded mx-auto flex items-center justify-center">
                    <Plus className="h-4 w-4" />
                  </div>
                </div>
              </div>
              
              <div className="mt-6 text-center">
                <div className="text-sm font-medium mb-2">Automate everyday tasks</div>
                <div className="text-sm font-medium">Easily integrate with existing tools.</div>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-2xl font-medium text-gray-900 mb-4">AI for Smarter Business Automation</h3>
            <p className="text-gray-600 mb-8">We create custom AI copilots tailored to your business, leveraging your data to automate tasks, answer questions, and solve challenges. They work alongside your team, making your work easier, faster, and more efficient. Learn more about <a href="/blog/5-processes-every-sme-should-be-automating-in-2025" className="text-violet-600 hover:text-violet-700 underline">processes you should be automating</a>.</p>
            
            <div className="grid grid-cols-4 gap-3">
              {[{
              icon: <Zap className="h-6 w-6" />,
              label: "ChatGPT",
              color: "bg-green-500"
            }, {
              icon: <MessageSquare className="h-6 w-6" />,
              label: "Claude",
              color: "bg-orange-500"
            }, {
              icon: <Code className="h-6 w-6" />,
              label: "GitHub Copilot",
              color: "bg-gray-800"
            }, {
              icon: <FileText className="h-6 w-6" />,
              label: "Notion AI",
              color: "bg-black"
            }, {
              icon: <Database className="h-6 w-6" />,
              label: "Airtable",
              color: "bg-yellow-500"
            }, {
              icon: <Calculator className="h-6 w-6" />,
              label: "Excel AI",
              color: "bg-green-600"
            }, {
              icon: <Mail className="h-6 w-6" />,
              label: "Gmail AI",
              color: "bg-red-500"
            }, {
              icon: <Link className="h-6 w-6" />,
              label: "Zapier",
              color: "bg-orange-600"
            }, {
              icon: <Phone className="h-6 w-6" />,
              label: "Zoom AI",
              color: "bg-blue-500"
            }, {
              text: "stripe",
              color: "bg-purple-600"
            }, {
              icon: <Palette className="h-6 w-6" />,
              label: "Figma AI",
              color: "bg-pink-500"
            }, {
              text: "700+",
              color: "bg-black"
            }].map((item, index) => <div key={index} className={`${item.color || 'bg-white border'} rounded-lg p-4 h-16 flex items-center justify-center text-white`}>
                  {item.text ? <span className="font-bold text-sm">
                      {item.text}
                    </span> : <div className="text-center">
                      {item.icon}
                    </div>}
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
};
